<script setup lang="ts">
import { computed, ref, shallowRef } from 'vue'
import { Check } from 'lucide-vue-next'
import copy from 'copy-to-clipboard'
import { storeToRefs } from 'pinia'
import { useSearchStore } from '@/stores/searchStoreV2'
import { useNotificationStore } from '@/stores/notificationStore'
import type { PromptTag, PromptType } from '@/types'
import { ENotificationType } from '@/types'

// Props definition
interface Props {
	word: string
	blackListMode?: boolean
	type?: PromptType
	emitMode?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	emitMode: false,
})

const emit = defineEmits(['unblacklist', 'tag'])
// State and Refs
const searchStore = useSearchStore()
const { prompts, draggingSource } = storeToRefs(searchStore)
const copied = shallowRef(false)
const chip = ref<HTMLElement | null>(null)

// Computed word index
const wordIndex = computed(() =>
	props.type ? prompts.value[props.type].findIndex((word: PromptTag) => word.value === props.word) : -1,
)

// Methods
function deleteChip(): void {
	if (props.blackListMode) {
		emit('unblacklist', props.word)
	}
	else if (props.type && wordIndex.value >= 0) {
		searchStore.deleteTag(props.word, props.type)
		searchStore.loadedSearch = 0
	}
}

function onDragStart(event: DragEvent, tag: string): void {
	draggingSource.value = props.type as PromptType
	event.dataTransfer?.setData('text/plain', tag)
}

let copyTimeoutId: ReturnType<typeof setTimeout>
function copyToken(token: string): void {
	if (props.emitMode) {
		emit('tag', token)
		return
	}
	copy(token)
	copied.value = true

	// Notify clipboard copy
	useNotificationStore().push(ENotificationType.App, {
		key: `notification.clipboard_token`,
		data: { token },
		delay: 1500,
	})

	// Reset copied state
	clearTimeout(copyTimeoutId)
	copyTimeoutId = setTimeout(() => (copied.value = false), 4000)
}
</script>

<template>
	<div
		ref="chip"
		class="chip"
		draggable="true"
		:class="copied ? 'border-emerald-400' : 'border-transparent'"
		@click="() => (props.type || props.blackListMode ? deleteChip() : copyToken(props.word))"
		@dragstart="($event) => onDragStart($event, props.word)"
		@dragover.prevent
		@dragend="draggingSource = null"
	>
		<span class="chip-content">{{ props.word }}</span>
		<Check
			v-if="copied"
			class="lucide-button-icon text-emerald-400"
		/>
	</div>
</template>

<style lang="scss" scoped>
.chip {
	@apply relative select-none cursor-pointer rounded-md bg-slate-700 w-fit h-fit px-2 py-1 shadow-md shadow-slate-950/60
	font-medium duration-300 hover:bg-slate-500 text-sm flex justify-center items-center gap-1;
}
</style>
